import { css, useTheme } from "@emotion/react";
import { IconBox } from "./IconBox";
import { useAnimatedThemeToggle, AnimationVariant, AnimationStart } from "hooks/useAnimatedThemeToggle";

type ThemeToggleProps = {
  size?: "small" | "medium" | "large";
  className?: string;
  variant?: AnimationVariant;
  animationStart?: AnimationStart;
  blur?: boolean;
  duration?: string;
};

export function ThemeToggle({
  size = "medium",
  className,
  variant = "rectangle",
  animationStart = "top-down",
  blur = false,
  duration = "0.4s"
}: ThemeToggleProps) {
  const emotionTheme = useTheme() as any;
  const { isDark, toggleTheme } = useAnimatedThemeToggle({
    variant,
    start: animationStart,
    blur,
    duration,
  });

  const boxDims = {
    small: { side: 32, radius: 6 },
    medium: { side: 40, radius: 8 },
    large: { side: 48, radius: 10 },
  }[size];
  const iconPx = Math.round(boxDims.side * 0.55);

  return (
    <button
      onClick={toggleTheme}
      className={className}
      css={css`
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 32px;
        width: 32px;
        border: none;
        border-radius: ${boxDims.radius}px;
        color: ${emotionTheme.palette.grey["100"]};
        cursor: pointer;
        transition: background-color 200ms ease-in-out, box-shadow 200ms ease-in-out, transform 80ms ease-out;

        &:hover {
          background-color: ${isDark ? "rgba(255, 255, 255, 0.16)" : "rgba(255, 255, 255, 0.16)"};
        }

        &:active {
          transform: translateY(1px);
        }

        &:focus-visible {
          outline: 2px solid rgba(99, 143, 255, 0.6);
          outline-offset: 2px;
        }
      `}
      aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
      title={`Switch to ${isDark ? "light" : "dark"} mode`}
    >
      {isDark ? (
        <IconBox color={emotionTheme.palette.grey["500"]} size="small" name="Moon" />
      ) : (
        <IconBox color={emotionTheme.palette.grey["500"]} size="medium" name="Sun" />
      )}
    </button>
  );
}
